import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Lock, Save, Clock, Calendar, Globe, Bell, ArrowLeftRight, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SectionVisibility from './SectionVisibility';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


export default function Settings() {
  const { user, token, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const [emailSettingsLoading, setEmailSettingsLoading] = useState(false);
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(false);
  const [emailData, setEmailData] = useState({
    new_email: user?.email || '',
    current_password: ''
  });
  
  const [preferences, setPreferences] = useState({
    time_format: user?.time_format || '24',
    date_format: user?.date_format || 'DD/MM/YYYY',
    default_view: user?.default_view || 'admin'
  });

  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_from: '',
    smtp_password: ''
  });

  const [notificationPrefs, setNotificationPrefs] = useState({
    ride_assigned: true,
    ride_status_changed: true,
    driver_actions: true,
    my_own_actions: false,
    email_ride_assigned: true,
    email_ride_status_changed: true,
    email_driver_actions: true,
    email_my_own_actions: false,
    push_ride_assigned: true,
    push_ride_status_changed: true,
    push_driver_actions: true,
    push_my_own_actions: false
  });

  // Auto-Live Time Settings
  const [autoLiveSettings, setAutoLiveSettings] = useState({
    auto_live_time: '18:00',
    auto_live_enabled: true
  });
  const [autoLiveLoading, setAutoLiveLoading] = useState(false);
  const [autoLiveTriggerLoading, setAutoLiveTriggerLoading] = useState(false);
  const [autoLiveStatus, setAutoLiveStatus] = useState(null);

  useEffect(() => {
    if (user) {
      setPreferences({
        time_format: user.time_format || '24',
        date_format: user.date_format || 'DD/MM/YYYY',
        default_view: user.default_view || 'admin'
      });
      // Load email settings and notification preferences
      fetchEmailSettings();
      fetchNotificationPreferences();
      fetchAutoLiveSettings();
    }
  }, [user]);

  const fetchEmailSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API}/auth/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationPrefs(response.data);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const fetchAutoLiveSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/auto-live-time`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoLiveSettings(response.data);
    } catch (error) {
      console.error('Error fetching auto-live settings:', error);
    }
    // Also fetch status
    try {
      const statusResp = await axios.get(`${API}/settings/auto-live-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoLiveStatus(statusResp.data);
    } catch (error) {
      console.error('Error fetching auto-live status:', error);
    }
  };

  const handleAutoLiveTrigger = async () => {
    setAutoLiveTriggerLoading(true);
    try {
      const response = await axios.post(`${API}/settings/auto-live-trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchAutoLiveSettings();
    } catch (error) {
      console.error('Error triggering auto-live:', error);
      toast.error(error.response?.data?.detail || 'Error triggering auto-live');
    } finally {
      setAutoLiveTriggerLoading(false);
    }
  };

  const handleAutoLiveUpdate = async (e) => {
    e.preventDefault();
    setAutoLiveLoading(true);
    try {
      await axios.put(`${API}/settings/auto-live-time`, autoLiveSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Auto-live settings updated successfully!');
    } catch (error) {
      console.error('Error updating auto-live settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update auto-live settings');
    } finally {
      setAutoLiveLoading(false);
    }
  };

  const handleNotificationPrefsUpdate = async (e) => {
    e.preventDefault();
    setNotificationPrefsLoading(true);
    try {
      await axios.put(`${API}/auth/notification-preferences`, notificationPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Notification preferences updated successfully!');
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      toast.error(error.response?.data?.detail || 'Failed to update notification preferences');
    } finally {
      setNotificationPrefsLoading(false);
    }
  };

  const handleEmailSettingsUpdate = async (e) => {
    e.preventDefault();
    
    setEmailSettingsLoading(true);
    try {
      await axios.put(`${API}/settings/email`, emailSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email settings updated successfully!');
    } catch (error) {
      console.error('Error updating email settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to update email settings');
    } finally {
      setEmailSettingsLoading(false);
    }
  };

  const handleEmailUpdate = async (e) => {
    e.preventDefault();
    
    if (!emailData.current_password) {
      toast.error('Please enter your current password');
      return;
    }

    if (emailData.new_email === user.email) {
      toast.error('New email is the same as current email');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/auth/update-email`,
        emailData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email updated successfully! Please login again.');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferencesUpdate = async (e) => {
    e.preventDefault();
    
    setPreferencesLoading(true);
    try {
      const response = await axios.put(
        `${API}/auth/update-preferences`,
        preferences,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update user in context with new preferences
      updateUser({ ...user, ...preferences });
      toast.success('Preferences updated successfully!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error(error.response?.data?.detail || 'Failed to update preferences');
    } finally {
      setPreferencesLoading(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-black" data-testid="settings-title">Settings</h1>
        <p className="text-gray-600 mt-2">Manage your account settings</p>
      </div>

      <div className="max-w-2xl space-y-6">
        {user?.is_super_admin && <SectionVisibility />}
        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Profile Information</h2>
              <p className="text-sm text-gray-600">View your account details</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-gray-700 text-sm mb-2 block">Name</Label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm text-gray-700">
                {user?.name}
              </div>
            </div>

            <div>
              <Label className="text-gray-700 text-sm mb-2 block">Current Email</Label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm text-gray-700">
                {user?.email}
              </div>
            </div>

            <div>
              <Label className="text-gray-700 text-sm mb-2 block">Role</Label>
              <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                  {user?.role}
                </span>
              </div>
            </div>

            {user?.phone && (
              <div>
                <Label className="text-gray-700 text-sm mb-2 block">Phone</Label>
                <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-sm text-gray-700">
                  {user.phone}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Date & Time Preferences</h2>
              <p className="text-sm text-gray-600">Customize how dates and times are displayed</p>
            </div>
          </div>

          <form onSubmit={handlePreferencesUpdate} className="space-y-5">
            <div>
              <Label htmlFor="time_format" className="text-gray-700 font-medium mb-2 block">
                Time Format
              </Label>
              <Select
                value={preferences.time_format}
                onValueChange={(value) => setPreferences({ ...preferences, time_format: value })}
              >
                <SelectTrigger className="h-11 rounded-sm border-blue-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12">12-hour (2:30 PM)</SelectItem>
                  <SelectItem value="24">24-hour (14:30)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="date_format" className="text-gray-700 font-medium mb-2 block">
                Date Format
              </Label>
              <Select
                value={preferences.date_format}
                onValueChange={(value) => setPreferences({ ...preferences, date_format: value })}
              >
                <SelectTrigger className="h-11 rounded-sm border-blue-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY (12/10/2024)</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY (10/12/2024)</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD (2024-12-10)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              disabled={preferencesLoading}
              className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-sm"
            >
              {preferencesLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Save Preferences
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Auto-Live Settings Section - Admin Only */}
        {(user?.role === 'admin' || user?.role === 'admin-driver') && (
          <div className="bg-white border border-green-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-black">Auto-Live Rides</h2>
                <p className="text-sm text-gray-600">Configure when tomorrow&apos;s rides become visible to drivers</p>
              </div>
            </div>

            <form onSubmit={handleAutoLiveUpdate} className="space-y-5">
              <div className="bg-green-50 border border-green-200 rounded-sm p-4 mb-4">
                <p className="text-sm text-green-800">
                  <strong>How it works:</strong> Drivers can only see rides that are marked as &quot;LIVE&quot;. 
                  Tomorrow&apos;s rides will automatically become LIVE at the time you set below. 
                  You can also manually set rides LIVE from the Rides page.
                </p>
              </div>

              <div>
                <Label htmlFor="auto_live_time" className="text-gray-700 font-medium mb-2 block">
                  Auto-Live Time
                </Label>
                <p className="text-xs text-gray-500 mb-2">
                  Tomorrow&apos;s rides will become visible to drivers at this time today
                </p>
                <Input
                  type="time"
                  id="auto_live_time"
                  value={autoLiveSettings.auto_live_time}
                  onChange={(e) => setAutoLiveSettings({ ...autoLiveSettings, auto_live_time: e.target.value })}
                  className="h-11 rounded-sm border-green-200 max-w-xs"
                />
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoLiveSettings.auto_live_enabled}
                    onChange={(e) => setAutoLiveSettings({ ...autoLiveSettings, auto_live_enabled: e.target.checked })}
                    className="w-4 h-4 text-green-600 rounded border-gray-300 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">Enable automatic scheduling</span>
                </label>
                <p className="text-xs text-gray-500 ml-7 mt-1">
                  If disabled, you&apos;ll need to manually set rides LIVE
                </p>
              </div>

              <Button
                type="submit"
                disabled={autoLiveLoading}
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-sm"
              >
                {autoLiveLoading ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Updating...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Save className="mr-2 h-5 w-5" />
                    Save Auto-Live Settings
                  </span>
                )}
              </Button>
            </form>

            {/* Manual Trigger & Status */}
            <div className="mt-6 pt-6 border-t border-green-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">Trigger Manuale</h3>
                  <p className="text-xs text-gray-500">Force LIVE activation of tomorrow&apos;s rides now</p>
                </div>
                <Button
                  type="button"
                  data-testid="auto-live-trigger-btn"
                  onClick={handleAutoLiveTrigger}
                  disabled={autoLiveTriggerLoading}
                  className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-sm"
                >
                  {autoLiveTriggerLoading ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Attivazione...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Zap className="mr-1.5 h-4 w-4" />
                      Force Auto-Live Now
                    </span>
                  )}
                </Button>
              </div>

              {autoLiveStatus && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-xs space-y-1" data-testid="auto-live-status-panel">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Athens Time:</span>
                    <span className="font-mono text-gray-800">{autoLiveStatus.current_athens_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Tomorrow's non-LIVE rides:</span>
                    <span className="font-semibold text-gray-800">{autoLiveStatus.tomorrow_pending_rides}</span>
                  </div>
                  {autoLiveStatus.recent_runs?.length > 0 ? (
                    <div className="pt-2 border-t border-gray-200">
                      <span className="text-gray-500 block mb-1">Recent runs:</span>
                      {autoLiveStatus.recent_runs.map((run, idx) => (
                        <div key={idx} className="flex justify-between text-gray-600">
                          <span>{run.run_date} {run.auto_live_time === 'manual_trigger' ? '(manual)' : `(${run.auto_live_time})`}</span>
                          <span>{run.rides_updated} rides</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pt-2 border-t border-gray-200 text-gray-400">
                      No runs recorded
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Default View Section - Admin-Driver Only */}
        {user?.role === 'admin-driver' && (
          <div className="bg-white border border-indigo-200 rounded-sm shadow-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <ArrowLeftRight className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-black">Default View</h2>
                <p className="text-sm text-gray-600">Choose which dashboard opens when you login</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/auth/update-preferences`, { default_view: 'admin' }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setPreferences({ ...preferences, default_view: 'admin' });
                      updateUser({ ...user, default_view: 'admin' });
                      toast.success('Default view set to Admin Dashboard');
                    } catch (error) {
                      toast.error('Failed to update default view');
                    }
                  }}
                  className={`p-4 border-2 rounded-sm transition-all ${
                    preferences.default_view === 'admin'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                  data-testid="default-view-admin"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                      preferences.default_view === 'admin' ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <User className="h-6 w-6" />
                    </div>
                    <span className={`font-medium ${
                      preferences.default_view === 'admin' ? 'text-indigo-700' : 'text-gray-700'
                    }`}>Admin Dashboard</span>
                    <span className="text-xs text-gray-500">Manage rides, users, reports</span>
                    {preferences.default_view === 'admin' && (
                      <span className="text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full">Current Default</span>
                    )}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/auth/update-preferences`, { default_view: 'driver' }, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setPreferences({ ...preferences, default_view: 'driver' });
                      updateUser({ ...user, default_view: 'driver' });
                      toast.success('Default view set to Driver Dashboard');
                    } catch (error) {
                      toast.error('Failed to update default view');
                    }
                  }}
                  className={`p-4 border-2 rounded-sm transition-all ${
                    preferences.default_view === 'driver'
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'
                  }`}
                  data-testid="default-view-driver"
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                      preferences.default_view === 'driver' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <Clock className="h-6 w-6" />
                    </div>
                    <span className={`font-medium ${
                      preferences.default_view === 'driver' ? 'text-emerald-700' : 'text-gray-700'
                    }`}>Driver Dashboard</span>
                    <span className="text-xs text-gray-500">View & manage your rides</span>
                    {preferences.default_view === 'driver' && (
                      <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Current Default</span>
                    )}
                  </div>
                </button>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-4">
                <p className="text-sm text-indigo-800">
                  <strong>Tip:</strong> You can always switch between Admin and Driver views using the switch button in the sidebar or header.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white border border-blue-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-sky-100 rounded-full flex items-center justify-center">
              <Mail className="h-6 w-6 text-sky-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Change Email</h2>
              <p className="text-sm text-gray-600">Update your email address</p>
            </div>
          </div>

          <form onSubmit={handleEmailUpdate} className="space-y-5" data-testid="email-update-form">
            <div>
              <Label htmlFor="new_email" className="text-gray-700 font-medium mb-2 block">
                New Email Address
              </Label>
              <Input
                id="new_email"
                type="email"
                value={emailData.new_email}
                onChange={(e) => setEmailData({ ...emailData, new_email: e.target.value })}
                placeholder="newemail@example.com"
                required
                className="h-11 rounded-sm border-blue-200 focus:ring-2 focus:ring-sky-500"
                data-testid="new-email-input"
              />
            </div>

            <div>
              <Label htmlFor="current_password" className="text-gray-700 font-medium mb-2 block">
                Current Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="current_password"
                  type="password"
                  value={emailData.current_password}
                  onChange={(e) => setEmailData({ ...emailData, current_password: e.target.value })}
                  placeholder="Enter your current password"
                  required
                  className="h-11 rounded-sm pl-10 border-blue-200 focus:ring-2 focus:ring-sky-500"
                  data-testid="current-password-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Required to confirm your identity</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After changing your email, you will be logged out and need to sign in again with your new email.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-black hover:bg-blue-900 text-white font-medium rounded-sm"
              data-testid="update-email-button"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Update Email
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white border border-amber-200 rounded-sm shadow-sm p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Bell className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-black">Notification Preferences</h2>
              <p className="text-sm text-gray-600">Choose which notifications you want to receive</p>
            </div>
          </div>

          <form onSubmit={handleNotificationPrefsUpdate} className="space-y-6">
            {/* In-App Notifications */}
            <div>
              <h3 className="font-medium text-gray-800 mb-3">In-App Notifications</h3>
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.ride_assigned}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, ride_assigned: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">When a ride is assigned</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.ride_status_changed}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, ride_status_changed: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">When a ride status changes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.driver_actions}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, driver_actions: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">When drivers perform actions (pickup, dropoff, etc.)</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.my_own_actions}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, my_own_actions: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">My own actions (create/edit rides)</span>
                </label>
              </div>
            </div>

            {/* Email Notifications */}
            <div>
              <h3 className="font-medium text-gray-800 mb-3">Email Notifications</h3>
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email_ride_assigned}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, email_ride_assigned: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Email me when a ride is assigned</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email_ride_status_changed}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, email_ride_status_changed: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Email me when a ride status changes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email_driver_actions}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, email_driver_actions: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Email me when drivers perform actions</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.email_my_own_actions}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, email_my_own_actions: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Email me for my own actions</span>
                </label>
              </div>
            </div>

            {/* Push Notifications */}
            <div>
              <h3 className="font-medium text-gray-800 mb-3">Push Notifications (Browser)</h3>
              <div className="space-y-3 pl-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push_ride_assigned}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, push_ride_assigned: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Push notification when a ride is assigned</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push_ride_status_changed}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, push_ride_status_changed: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Push notification when a ride status changes</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push_driver_actions}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, push_driver_actions: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Push notification when drivers perform actions</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationPrefs.push_my_own_actions}
                    onChange={(e) => setNotificationPrefs({ ...notificationPrefs, push_my_own_actions: e.target.checked })}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">Push notification for my own actions</span>
                </label>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Email notifications require email settings below. Push notifications require browser permission (prompted on login).
              </p>
            </div>

            <Button
              type="submit"
              disabled={notificationPrefsLoading}
              className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-sm"
            >
              {notificationPrefsLoading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Save className="mr-2 h-5 w-5" />
                  Save Notification Preferences
                </span>
              )}
            </Button>
          </form>
        </div>

        {/* Email Notification Settings - Admin Only */}
        {user?.role === 'admin' && (
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6">
            <h2 className="text-xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Notification Settings
            </h2>

            <form onSubmit={handleEmailSettingsUpdate} className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-sm">
                <input
                  type="checkbox"
                  id="email_enabled"
                  checked={emailSettings.enabled}
                  onChange={(e) => setEmailSettings({ ...emailSettings, enabled: e.target.checked })}
                  className="w-5 h-5 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer"
                />
                <label htmlFor="email_enabled" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Enable email notifications for drivers
                </label>
              </div>

              {emailSettings.enabled && (
                <>
                  {/* Gmail Help Text */}
                  <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">Gmail Setup:</h3>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p><strong>Option 1: If your Gmail has 2-Factor Authentication (2FA):</strong></p>
                      <ol className="list-decimal list-inside space-y-1 ml-4">
                        <li>Go to: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="underline">myaccount.google.com/apppasswords</a></li>
                        <li>Create App Password for "Mail"</li>
                        <li>Copy the 16-character code (remove spaces)</li>
                        <li>Paste in "Password" field below</li>
                      </ol>
                      <p className="mt-2"><strong>Option 2: If your Gmail does NOT have 2FA:</strong></p>
                      <p className="ml-4">Just enter your regular Gmail password</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Gmail Address */}
                    <div>
                      <Label htmlFor="smtp_user" className="text-slate-700 font-medium mb-2 block">
                        Gmail Address
                      </Label>
                      <Input
                        id="smtp_user"
                        type="email"
                        value={emailSettings.smtp_user}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value, smtp_from: e.target.value })}
                        placeholder="your-email@gmail.com"
                        className="h-11 rounded-sm"
                        required
                      />
                    </div>

                    {/* Gmail Password */}
                    <div>
                      <Label htmlFor="smtp_password" className="text-slate-700 font-medium mb-2 block">
                        Gmail Password
                      </Label>
                      <Input
                        id="smtp_password"
                        type="password"
                        value={emailSettings.smtp_password}
                        onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                        placeholder="Password or App Password"
                        className="h-11 rounded-sm"
                      />
                      <p className="text-xs text-slate-500 mt-1">App Password (if 2FA) or regular password. Leave blank to keep current</p>
                    </div>
                  </div>

                  {/* Advanced Settings (Collapsed) */}
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900">
                      Advanced Settings (Optional)
                    </summary>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 pl-4">
                      <div>
                        <Label htmlFor="smtp_host" className="text-slate-700 font-medium mb-2 block">
                          SMTP Host
                        </Label>
                        <Input
                          id="smtp_host"
                          type="text"
                          value={emailSettings.smtp_host}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                          className="h-11 rounded-sm"
                        />
                      </div>

                      <div>
                        <Label htmlFor="smtp_port" className="text-slate-700 font-medium mb-2 block">
                          SMTP Port
                        </Label>
                        <Input
                          id="smtp_port"
                          type="number"
                          value={emailSettings.smtp_port}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) })}
                          className="h-11 rounded-sm"
                        />
                      </div>
                    </div>
                  </details>
                </>
              )}

              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-11 px-6"
                disabled={emailSettingsLoading}
              >
                {emailSettingsLoading ? (
                  <>
                    <span className="mr-2">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Email Settings
                  </>
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
